import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Truck, 
  Calendar,
  CheckCircle2,
  Circle,
  MapPin,
  Clock,
  Package,
  Plus,
  ChevronRight,
  ChevronLeft,
  PlayCircle,
  FileText,
  Trash2,
  Navigation,
  Phone,
  User,
  AlertCircle,
  CheckSquare,
  Square,
  Route,
  Printer,
  ExternalLink,
  MessageSquare,
  RotateCcw,
  XCircle,
  ClipboardCheck
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { getDeliveryStopCardClass, getDeliveryStopDotClass } from '../../lib/statusHelpers';
import { buildRouteHTML } from './delivery/printRoute';
import { openPrintWindow } from '../../lib/printWindow';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { staffService } from '../../services/staffService';
import { toast } from 'sonner';
import { PickupReconciliationDialog } from '../../components/verification/PickupReconciliationDialog';
import { CompleteDeliveryDialog } from '../../components/verification/CompleteDeliveryDialog';
import { OrderPhotoUpload } from '../../components/verification/OrderPhotoUpload';
import { PhotoProofGallery } from '../../components/verification/PhotoProofGallery';
import { LocationLine } from '../../components/LocationLine';

const ROUTE_STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700', icon: Circle },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: PlayCircle },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle2 }
};

const STOP_STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700' },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-700' },
  failed: { label: 'Failed Delivery', color: 'bg-orange-100 text-orange-700' }
};

const StaffDelivery = () => {
  const [activeTab, setActiveTab] = useState('orders');
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Orders ready for delivery
  const [ordersReady, setOrdersReady] = useState([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  
  // Delivery routes
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [routeDetailOpen, setRouteDetailOpen] = useState(false);
  
  // Create route dialog
  const [createRouteOpen, setCreateRouteOpen] = useState(false);
  const [driverName, setDriverName] = useState('');
  const [routeNotes, setRouteNotes] = useState('');
  const [creating, setCreating] = useState(false);

  // Stop update dialog
  const [stopUpdateOpen, setStopUpdateOpen] = useState(false);
  const [stopUpdateIndex, setStopUpdateIndex] = useState(null);
  const [stopUpdateStatus, setStopUpdateStatus] = useState('');
  const [stopUpdateNotes, setStopUpdateNotes] = useState('');
  const [stopUpdateCause, setStopUpdateCause] = useState('');
  const [stopUpdatePhotos, setStopUpdatePhotos] = useState([]);
  const [updating, setUpdating] = useState(false);

  // Verification dialogs (pickup count + delivery photo gate)
  const [reconcileStop, setReconcileStop] = useState(null); // { stop, index }
  const [completeStop, setCompleteStop] = useState(null);   // { stop, index }

  const today = new Date().toISOString().split('T')[0];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'orders') {
        const orders = await staffService.getOrdersReadyForDelivery(selectedDate);
        setOrdersReady(orders);
      }
      const routesData = await staffService.getDeliveryRoutes({ date: selectedDate });
      setRoutes(routesData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load delivery data');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, activeTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleOrderSelection = (orderId) => {
    const newSelected = new Set(selectedOrderIds);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrderIds(newSelected);
  };

  const toggleSelectAll = () => {
    const availableOrders = ordersReady.filter(o => !o.is_in_route);
    if (selectedOrderIds.size === availableOrders.length) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(availableOrders.map(o => o.id)));
    }
  };

  const handleCreateRoute = async () => {
    if (selectedOrderIds.size === 0) {
      toast.error('Please select at least one order');
      return;
    }

    setCreating(true);
    try {
      const result = await staffService.createDeliveryRoute({
        delivery_date: selectedDate,
        order_ids: Array.from(selectedOrderIds),
        driver_name: driverName,
        notes: routeNotes
      });
      toast.success(`Route ${result.route_number} created`);
      setCreateRouteOpen(false);
      setSelectedOrderIds(new Set());
      setDriverName('');
      setRouteNotes('');
      loadData();
    } catch (error) {
      console.error('Error creating route:', error);
      toast.error('Failed to create delivery route');
    } finally {
      setCreating(false);
    }
  };

  const handleStartRoute = async (routeId) => {
    try {
      await staffService.startDeliveryRoute(routeId);
      toast.success('Route started');
      loadData();
    } catch (error) {
      toast.error('Failed to start route');
    }
  };

  const handleViewRoute = async (route) => {
    setSelectedRoute(route);
    setRouteDetailOpen(true);
  };

  // Re-fetch the open route (fresh verification state) + list
  const refreshSelectedRoute = useCallback(async () => {
    if (selectedRoute?.id) {
      try {
        const fresh = await staffService.getDeliveryRoute(selectedRoute.id);
        setSelectedRoute(fresh);
      } catch (error) {
        console.error('Error refreshing route:', error);
      }
    }
    loadData();
  }, [selectedRoute?.id, loadData]);

  // Open the stop update dialog
  const openStopUpdateDialog = (stopIndex, status, existingNotes = '') => {
    setStopUpdateIndex(stopIndex);
    setStopUpdateStatus(status);
    setStopUpdateNotes(existingNotes || '');
    setStopUpdateCause(selectedRoute?.stops?.[stopIndex]?.failure_cause || '');
    setStopUpdatePhotos(selectedRoute?.stops?.[stopIndex]?.delivery_photos || []);
    setStopUpdateOpen(true);
  };

  // Handle the actual stop update with notes
  const handleUpdateStop = async () => {
    if (!selectedRoute || stopUpdateIndex === null) return;
    
    setUpdating(true);
    try {
      const result = await staffService.updateDeliveryStop(
        selectedRoute.id, 
        stopUpdateIndex, 
        { 
          status: stopUpdateStatus,
          notes: stopUpdateNotes,
          failure_cause: stopUpdateStatus === 'failed' ? stopUpdateCause : null,
        }
      );
      
      // Update local state
      const updatedStops = [...selectedRoute.stops];
      updatedStops[stopUpdateIndex] = { 
        ...updatedStops[stopUpdateIndex], 
        status: stopUpdateStatus,
        notes: stopUpdateNotes,
        failure_cause: stopUpdateStatus === 'failed' ? stopUpdateCause : null,
        delivery_photos: stopUpdatePhotos,
        delivered_at: stopUpdateStatus === 'delivered' ? new Date().toISOString() : updatedStops[stopUpdateIndex].delivered_at,
        failed_at: stopUpdateStatus === 'failed' ? new Date().toISOString() : updatedStops[stopUpdateIndex].failed_at
      };
      
      setSelectedRoute({
        ...selectedRoute,
        stops: updatedStops,
        completed_stops: result.completed_stops,
        status: result.route_status
      });
      
      const statusLabel = stopUpdateStatus === 'delivered' ? 'Delivered' : 'Failed Delivery';
      toast.success(`Stop marked as ${statusLabel}`);
      setStopUpdateOpen(false);
      setStopUpdateNotes('');
      loadData(); // Refresh the list
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update stop', { position: 'top-center', duration: 4000 });
    } finally {
      setUpdating(false);
    }
  };

  // Quick update without notes (for simple delivered marking)
  const handleQuickUpdateStop = async (stopIndex, status) => {
    if (!selectedRoute) return;
    
    try {
      const result = await staffService.updateDeliveryStop(selectedRoute.id, stopIndex, { status });
      
      // Update local state
      const updatedStops = [...selectedRoute.stops];
      updatedStops[stopIndex] = { 
        ...updatedStops[stopIndex], 
        status,
        delivered_at: status === 'delivered' ? new Date().toISOString() : null
      };
      
      setSelectedRoute({
        ...selectedRoute,
        stops: updatedStops,
        completed_stops: result.completed_stops,
        status: result.route_status
      });
      
      toast.success(`Stop marked as ${status === 'delivered' ? 'Delivered' : 'Failed'}`);
      loadData(); // Refresh the list
    } catch (error) {
      toast.error('Failed to update stop');
    }
  };

  const handleDeleteRoute = async (routeId) => {
    if (!window.confirm('Are you sure you want to cancel this route? ALL orders (including delivered and failed) will be returned to "Ready for Delivery" status.')) return;
    
    try {
      await staffService.deleteDeliveryRoute(routeId);
      toast.success('Route cancelled. All orders returned to Ready for Delivery.');
      setRouteDetailOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to cancel route');
    }
  };

  const openGoogleMaps = (address) => {
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
  };

  const printRoute = () => {
    if (!selectedRoute) return;
    openPrintWindow(buildRouteHTML(selectedRoute));
  };

  // Calculate summary for selected orders
  const selectedOrdersSummary = useMemo(() => {
    const selected = ordersReady.filter(o => selectedOrderIds.has(o.id));
    return {
      count: selected.length,
      totalItems: selected.reduce((sum, o) => sum + o.total_items, 0),
      totalValue: selected.reduce((sum, o) => sum + o.total_amount, 0)
    };
  }, [ordersReady, selectedOrderIds]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="delivery-loading">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-delivery">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Delivery</h1>
          <p className="text-sm text-gray-600 mt-1">Manage delivery routes and track deliveries</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="orders" className="flex items-center gap-2 min-w-0 text-xs sm:text-sm">
            <Package className="h-4 w-4 shrink-0" />
            <span className="truncate">Ready for Delivery</span>
          </TabsTrigger>
          <TabsTrigger value="routes" className="flex items-center gap-2 min-w-0 text-xs sm:text-sm">
            <Route className="h-4 w-4 shrink-0" />
            <span className="truncate">Routes</span>
          </TabsTrigger>
        </TabsList>

        {/* Orders Ready for Delivery Tab */}
        <TabsContent value="orders" className="space-y-4 mt-4">
          {/* Date Picker and Actions */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-40"
                  data-testid="delivery-date-picker"
                />
              </div>
              {selectedDate === today && (
                <Badge className="bg-green-100 text-green-700">Today</Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedOrderIds.size > 0 && (
                <Button
                  onClick={() => setCreateRouteOpen(true)}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="create-route-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Route ({selectedOrderIds.size})
                </Button>
              )}
            </div>
          </div>

          {/* Selected Orders Summary */}
          {selectedOrderIds.size > 0 && (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Badge className="bg-orange-100 text-orange-700">
                      {selectedOrdersSummary.count} orders selected
                    </Badge>
                    <span className="text-sm text-gray-600">
                      {selectedOrdersSummary.totalItems} items • ${selectedOrdersSummary.totalValue.toFixed(2)}
                    </span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedOrderIds(new Set())}>
                    Clear selection
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Orders List */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="h-5 w-5 text-orange-600" />
                  Orders Ready for Delivery ({ordersReady.filter(o => !o.is_in_route).length})
                </CardTitle>
                {ordersReady.filter(o => !o.is_in_route).length > 0 && (
                  <Button variant="outline" size="sm" onClick={toggleSelectAll}>
                    {selectedOrderIds.size === ordersReady.filter(o => !o.is_in_route).length 
                      ? 'Deselect All' 
                      : 'Select All'}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {ordersReady.length > 0 ? (
                <div className="space-y-3">
                  {ordersReady.map((order) => {
                    const isSelected = selectedOrderIds.has(order.id);
                    const isInRoute = order.is_in_route;
                    
                    return (
                      <div
                        key={order.id}
                        onClick={() => !isInRoute && toggleOrderSelection(order.id)}
                        className={`p-4 rounded-lg border transition-all ${
                          isInRoute 
                            ? 'bg-gray-50 opacity-60 cursor-not-allowed'
                            : isSelected
                              ? 'bg-orange-50 border-orange-300 cursor-pointer'
                              : 'bg-white border-gray-200 cursor-pointer hover:border-orange-200'
                        }`}
                        data-testid={`order-${order.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`flex items-center justify-center w-6 h-6 rounded border-2 flex-shrink-0 mt-1 ${
                            isInRoute 
                              ? 'border-gray-300 bg-gray-200'
                              : isSelected 
                                ? 'border-orange-500 bg-orange-500' 
                                : 'border-gray-400 bg-white'
                          }`}>
                            {isInRoute ? (
                              <span className="text-gray-400 text-xs">—</span>
                            ) : isSelected ? (
                              <CheckCircle2 className="h-4 w-4 text-white" />
                            ) : null}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900">{order.client_name}</span>
                                <Badge variant="outline" className="text-xs">{order.order_number}</Badge>
                                {isInRoute && (
                                  <Badge className="bg-blue-100 text-blue-700 text-xs">In Route</Badge>
                                )}
                                {order.needs_redelivery && (
                                  <Badge
                                    className="bg-orange-100 text-orange-700 text-xs"
                                    data-testid={`redelivery-badge-${order.id}`}
                                  >
                                    Re-delivery · attempt {(order.failed_attempts || 0) + 1}
                                  </Badge>
                                )}
                              </div>
                              <span className="font-semibold text-gray-900">${order.total_amount.toFixed(2)}</span>
                            </div>
                            
                            <LocationLine
                              name={order.location_name}
                              source={order.location_name_source}
                              clientName={order.client_name}
                              className="mb-2"
                              testId={`order-location-${order.id}`}
                            />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                              <div className="flex items-start gap-2 text-gray-600">
                                <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                <span className="truncate">{order.delivery_address || 'No address'}</span>
                              </div>
                              {order.delivery_time_window && (
                                <div className="flex items-center gap-2 text-gray-600">
                                  <Clock className="h-4 w-4 text-gray-400" />
                                  <span>{order.delivery_time_window}</span>
                                </div>
                              )}
                            </div>
                            
                            {order.delivery_instructions && (
                              <div className="mt-2 p-2 bg-amber-50 rounded text-sm text-amber-800">
                                <AlertCircle className="h-3 w-3 inline mr-1" />
                                {order.delivery_instructions}
                              </div>
                            )}

                            {order.needs_redelivery && order.previous_failure && (
                              <div
                                className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-sm text-orange-900"
                                data-testid={`previous-failure-${order.id}`}
                              >
                                <AlertCircle className="h-3 w-3 inline mr-1" />
                                Last attempt failed ({order.previous_failure.cause_label})
                                {order.previous_failure.notes ? ` — ${order.previous_failure.notes}` : ''}
                                . Add it to a route to try again.
                              </div>
                            )}
                            
                            <div className="mt-2 flex flex-wrap gap-2">
                              {order.items.slice(0, 3).map((item) => (
                                <Badge key={item.product_name} variant="outline" className="text-xs">
                                  {item.product_name}: {item.quantity}
                                </Badge>
                              ))}
                              {order.items.length > 3 && (
                                <Badge variant="outline" className="text-xs text-gray-500">
                                  +{order.items.length - 3} more
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Orders Ready</h3>
                  <p className="text-gray-500">No orders are ready for delivery on this date</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Routes Tab */}
        <TabsContent value="routes" className="space-y-4 mt-4">
          {/* Date Picker */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-40"
              />
            </div>
            {selectedDate === today && (
              <Badge className="bg-green-100 text-green-700">Today</Badge>
            )}
          </div>

          {/* Routes List */}
          <div className="space-y-4">
            {routes.length > 0 ? (
              routes.map((route) => {
                const statusConfig = ROUTE_STATUS_CONFIG[route.status] || ROUTE_STATUS_CONFIG.pending;
                const StatusIcon = statusConfig.icon;
                const progress = route.total_stops > 0 
                  ? Math.round((route.completed_stops / route.total_stops) * 100) 
                  : 0;
                
                return (
                  <Card 
                    key={route.id} 
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleViewRoute(route)}
                    data-testid={`route-${route.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-4 min-w-0">
                          <div className={`p-3 rounded-lg shrink-0 ${statusConfig.color.split(' ')[0]}`}>
                            <StatusIcon className={`h-6 w-6 ${statusConfig.color.split(' ')[1]}`} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-lg">{route.route_number}</span>
                              <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                            </div>
                            <div className="text-sm text-gray-500 flex items-center gap-3 mt-1 flex-wrap">
                              {route.driver_name && (
                                <span className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  {route.driver_name}
                                </span>
                              )}
                              <span>{route.total_stops} stops</span>
                              <span>${route.stops.reduce((sum, s) => sum + s.total_amount, 0).toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="text-2xl font-bold text-gray-900">
                              {route.completed_stops}/{route.total_stops}
                            </div>
                            <div className="text-xs text-gray-500">completed</div>
                          </div>
                          <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-green-500 transition-all"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            ) : (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <Route className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Routes</h3>
                    <p className="text-gray-500 mb-4">No delivery routes for this date</p>
                    <Button
                      variant="outline"
                      onClick={() => setActiveTab('orders')}
                    >
                      View Orders Ready for Delivery
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Route Dialog */}
      <Dialog open={createRouteOpen} onOpenChange={setCreateRouteOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Delivery Route</DialogTitle>
            <DialogDescription>
              Create a route for {selectedOrderIds.size} selected orders
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="p-4 bg-orange-50 rounded-lg">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-orange-600">{selectedOrdersSummary.count}</div>
                  <div className="text-xs text-gray-500">Stops</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-orange-600">{selectedOrdersSummary.totalItems}</div>
                  <div className="text-xs text-gray-500">Items</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-orange-600">${selectedOrdersSummary.totalValue.toFixed(2)}</div>
                  <div className="text-xs text-gray-500">Value</div>
                </div>
              </div>
            </div>
            
            <div>
              <Label>Driver Name (optional)</Label>
              <Input
                value={driverName}
                onChange={(e) => setDriverName(e.target.value)}
                placeholder="Enter driver name"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Route Notes (optional)</Label>
              <Textarea
                value={routeNotes}
                onChange={(e) => setRouteNotes(e.target.value)}
                placeholder="Add any notes for this route"
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setCreateRouteOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateRoute}
                disabled={creating}
                className="bg-orange-500 hover:bg-orange-600"
              >
                {creating ? 'Creating...' : 'Create Route'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Route Detail Dialog */}
      <Dialog open={routeDetailOpen} onOpenChange={setRouteDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedRoute && (
            <>
              <DialogHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <DialogTitle className="flex items-center gap-2 flex-wrap">
                      {selectedRoute.route_number}
                      <Badge className={ROUTE_STATUS_CONFIG[selectedRoute.status]?.color}>
                        {ROUTE_STATUS_CONFIG[selectedRoute.status]?.label}
                      </Badge>
                    </DialogTitle>
                    <DialogDescription className="sr-only">{ROUTE_STATUS_CONFIG[selectedRoute.status]?.label} dialog. Use the form below to continue.</DialogDescription>
                    <p className="text-sm text-gray-500 mt-1">
                      {new Date(selectedRoute.delivery_date + 'T12:00:00').toLocaleDateString('en-US', {
                        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
                      })}
                      {selectedRoute.driver_name && ` • Driver: ${selectedRoute.driver_name}`}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={printRoute}>
                      <Printer className="h-4 w-4 mr-2" />
                      Print
                    </Button>
                    {selectedRoute.status === 'pending' && (
                      <Button 
                        size="sm" 
                        className="bg-blue-500 hover:bg-blue-600"
                        onClick={() => handleStartRoute(selectedRoute.id)}
                      >
                        <PlayCircle className="h-4 w-4 mr-2" />
                        Start Route
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-500 border-red-200 hover:bg-red-50"
                      onClick={() => handleDeleteRoute(selectedRoute.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Cancel Route
                    </Button>
                  </div>
                </div>
              </DialogHeader>
              
              {/* Progress */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Delivery Progress</span>
                  <span className="text-sm text-gray-500">
                    {selectedRoute.completed_stops} of {selectedRoute.total_stops} completed
                  </span>
                </div>
                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 transition-all"
                    style={{ 
                      width: `${selectedRoute.total_stops > 0 
                        ? (selectedRoute.completed_stops / selectedRoute.total_stops) * 100 
                        : 0}%` 
                    }}
                  />
                </div>
              </div>
              
              {/* Stops */}
              <div className="mt-4 space-y-3">
                <h4 className="font-medium text-gray-900">Delivery Stops</h4>
                {selectedRoute.stops.map((stop, index) => {
                  const stopStatus = STOP_STATUS_CONFIG[stop.status] || STOP_STATUS_CONFIG.pending;
                  
                  return (
                    <div 
                      key={stop.order_id || `stop-${index}`}
                      className={`p-4 rounded-lg border ${getDeliveryStopCardClass({ status: stop.status, isSelected: false })}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${getDeliveryStopDotClass({ status: stop.status, isSelected: false })}`}>
                            {stop.status === 'delivered' ? (
                              <CheckCircle2 className="h-5 w-5" />
                            ) : (
                              stop.stop_number
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-semibold">{stop.client_name}</span>
                              <Badge variant="outline" className="text-xs">{stop.order_number}</Badge>
                              <Badge className={stopStatus.color}>{stopStatus.label}</Badge>
                              {!stop.is_custom && stop.client_type_name && (
                                stop.guaranteed_returns ? (
                                  <Badge className="bg-purple-100 text-purple-700 text-xs" data-testid={`tier-badge-${stop.order_id}`}>
                                    Guaranteed Returns
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs text-gray-600" data-testid={`tier-badge-${stop.order_id}`}>
                                    {stop.client_type_name}
                                  </Badge>
                                )
                              )}
                            </div>
                            
                            <LocationLine
                              name={stop.location_name}
                              source={stop.location_name_source}
                              clientName={stop.client_name}
                              className="mb-1"
                              testId={`stop-location-${stop.order_id}`}
                            />

                            <div className="text-sm text-gray-600 space-y-1">
                              <div className="flex items-start gap-2">
                                <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                <span>{stop.delivery_address || 'No address'}</span>
                                {stop.delivery_address && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openGoogleMaps(stop.delivery_address);
                                    }}
                                  >
                                    <Navigation className="h-3 w-3 mr-1" />
                                    Navigate
                                  </Button>
                                )}
                              </div>
                              
                              {stop.delivery_time_window && (
                                <div className="flex items-center gap-2">
                                  <Clock className="h-4 w-4 text-gray-400" />
                                  <span>{stop.delivery_time_window}</span>
                                </div>
                              )}
                            </div>
                            
                            {stop.delivery_instructions && (
                              <div className="mt-2 p-2 bg-amber-50 rounded text-sm text-amber-800">
                                <AlertCircle className="h-3 w-3 inline mr-1" />
                                {stop.delivery_instructions}
                              </div>
                            )}
                            
                            <div className="mt-2 flex flex-wrap gap-2">
                              {stop.items.map((item) => (
                                <Badge key={item.product_name} variant="outline" className="text-xs">
                                  {item.product_name}: {item.quantity}
                                </Badge>
                              ))}
                            </div>
                            
                            {/* Pickup reconciliation status */}
                            {stop.pickup_reconciliation && (
                              <div className="mt-2 text-xs text-blue-700 flex items-center gap-1 flex-wrap" data-testid={`recon-status-${stop.order_id}`}>
                                <ClipboardCheck className="h-3 w-3" />
                                Pickup count recorded
                                {stop.pickup_reconciliation.has_changes && (
                                  <span className="font-medium text-amber-700">
                                    • {(stop.pickup_reconciliation.lines || []).filter((l) => l.delta !== 0).length} item(s) changed
                                  </span>
                                )}
                              </div>
                            )}
                            
                            {/* Show delivery/failure timestamp */}
                            {stop.delivered_at && (
                              <div className="mt-2 text-xs text-green-600">
                                <CheckCircle2 className="h-3 w-3 inline mr-1" />
                                Delivered at {new Date(stop.delivered_at).toLocaleTimeString()}
                              </div>
                            )}
                            {stop.failed_at && stop.status === 'failed' && (
                              <div className="mt-2 text-xs text-orange-600">
                                <XCircle className="h-3 w-3 inline mr-1" />
                                Failed at {new Date(stop.failed_at).toLocaleTimeString()}
                              </div>
                            )}
                            
                            {/* Show notes if any */}
                            {stop.notes && (
                              <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-700">
                                <MessageSquare className="h-3 w-3 inline mr-1" />
                                <span className="font-medium">Notes:</span> {stop.notes}
                              </div>
                            )}

                            {/* Permanent proof strip — stays readable after the
                                job is done, which is exactly when a driver
                                gets challenged on what was dropped off. */}
                            {!stop.is_custom
                              && (stop.status === 'delivered' || stop.status === 'failed')
                              && ((stop.delivery_photos || []).length > 0 || (stop.production_photos || []).length > 0) && (
                              <div className="mt-3 pt-3 border-t" data-testid={`stop-proof-${stop.order_id}`}>
                                <PhotoProofGallery
                                  testIdPrefix={`stop-proof-${stop.order_id}`}
                                  groups={[
                                    {
                                      key: 'production',
                                      title: 'Packed in the kitchen',
                                      caption: 'what left the bakery',
                                      photos: stop.production_photos || [],
                                    },
                                    {
                                      key: 'delivery',
                                      title: stop.status === 'failed' ? 'At the failed attempt' : 'At the door',
                                      caption: 'what arrived',
                                      photos: stop.delivery_photos || [],
                                    },
                                  ]}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-2 items-end">
                          <span className="text-lg font-semibold">${stop.total_amount.toFixed(2)}</span>
                          
                          {/* Pending stops: pickup count, then Delivered (photo gate) and Failed */}
                          {stop.status === 'pending' && (
                            <div className="flex flex-col gap-1">
                              {!stop.is_custom && stop.order_id && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-blue-600 border-blue-300 h-9"
                                  data-testid={`pickup-reconciliation-${stop.order_id}`}
                                  onClick={() => setReconcileStop({ stop, index })}
                                >
                                  <ClipboardCheck className="h-3 w-3 mr-1" />
                                  {stop.pickup_reconciliation ? 'Edit Count' : 'Pickup Count'}
                                </Button>
                              )}
                              <Button
                                size="sm"
                                className="bg-green-500 hover:bg-green-600 h-9"
                                data-testid={`delivered-btn-${stop.order_id || index}`}
                                onClick={() => stop.is_custom
                                  ? handleQuickUpdateStop(index, 'delivered')
                                  : setCompleteStop({ stop, index })}
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Delivered
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-orange-600 border-orange-300 h-9"
                                onClick={() => openStopUpdateDialog(index, 'failed', '')}
                                data-testid={`failed-btn-${stop.order_id || index}`}
                              >
                                <XCircle className="h-3 w-3 mr-1" />
                                Failed
                              </Button>
                            </div>
                          )}
                          
                          {/* Failed stops: allow re-attempt (change to delivered) */}
                          {stop.status === 'failed' && (
                            <div className="flex flex-col gap-1">
                              <Button
                                size="sm"
                                className="bg-green-500 hover:bg-green-600 h-9"
                                onClick={() => stop.is_custom
                                  ? openStopUpdateDialog(index, 'delivered', stop.notes || '')
                                  : setCompleteStop({ stop, index })}
                              >
                                <RotateCcw className="h-3 w-3 mr-1" />
                                Re-deliver
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-gray-500 h-7 text-xs"
                                onClick={() => openStopUpdateDialog(index, 'failed', stop.notes || '')}
                              >
                                <MessageSquare className="h-3 w-3 mr-1" />
                                Edit Notes
                              </Button>
                            </div>
                          )}
                          
                          {/* Delivered stops: allow adding notes */}
                          {stop.status === 'delivered' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-gray-500 h-7 text-xs"
                              onClick={() => openStopUpdateDialog(index, 'delivered', stop.notes || '')}
                            >
                              <MessageSquare className="h-3 w-3 mr-1" />
                              {stop.notes ? 'Edit Notes' : 'Add Notes'}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Stop Update Dialog - for adding notes when marking delivered/failed */}
      <Dialog open={stopUpdateOpen} onOpenChange={setStopUpdateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {stopUpdateStatus === 'delivered' ? 'Mark as Delivered' : 'Mark as Failed Delivery'}
            </DialogTitle>
            <DialogDescription>
              {stopUpdateStatus === 'delivered' 
                ? 'Confirm delivery and optionally add notes about this delivery.'
                : 'Record why this delivery failed. A notification will be sent to the client.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {stopUpdateIndex !== null && selectedRoute?.stops?.[stopUpdateIndex] && (
              <div className="rounded-lg border bg-gray-50 p-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  {selectedRoute.stops[stopUpdateIndex].client_name}
                  <Badge variant="outline" className="text-xs">
                    {selectedRoute.stops[stopUpdateIndex].order_number}
                  </Badge>
                </div>
                <LocationLine
                  name={selectedRoute.stops[stopUpdateIndex].location_name}
                  source={selectedRoute.stops[stopUpdateIndex].location_name_source}
                  clientName={selectedRoute.stops[stopUpdateIndex].client_name}
                  address={selectedRoute.stops[stopUpdateIndex].delivery_address}
                  className="mt-1"
                  testId={`stop-dialog-location-${selectedRoute.stops[stopUpdateIndex].order_id}`}
                />
              </div>
            )}
            <div>
              <Label htmlFor="stop-notes">
                {stopUpdateStatus === 'delivered' ? 'Delivery Notes (optional)' : 'Failure Reason'}
              </Label>
              <Textarea
                id="stop-notes"
                value={stopUpdateNotes}
                onChange={(e) => setStopUpdateNotes(e.target.value)}
                placeholder={stopUpdateStatus === 'delivered' 
                  ? 'e.g., Left with front desk, signed by manager...'
                  : 'e.g., Business closed, no one available to receive, wrong address...'
                }
                className="mt-1"
                rows={3}
              />
              {stopUpdateStatus === 'failed' && !stopUpdateNotes && (
                <p className="text-xs text-orange-600 mt-1">
                  Please provide a reason for the failed delivery.
                </p>
              )}
            </div>

            {stopUpdateStatus === 'failed' && (
              <div>
                <Label>What caused the failure?</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  {[
                    { value: 'client_caused', label: 'Client-caused', hint: 'Closed, refused, no one there' },
                    { value: 'bakery_caused', label: 'Bakery-caused', hint: 'Late, wrong address, our error' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setStopUpdateCause(option.value)}
                      className={`text-left rounded-lg border p-3 min-h-[44px] transition-colors ${
                        stopUpdateCause === option.value
                          ? 'border-orange-400 bg-orange-50'
                          : 'border-gray-200 hover:border-orange-200'
                      }`}
                      data-testid={`failure-cause-${option.value}`}
                    >
                      <span className="block text-sm font-medium text-gray-900">{option.label}</span>
                      <span className="block text-xs text-gray-500">{option.hint}</span>
                    </button>
                  ))}
                </div>
                {!stopUpdateCause && (
                  <p className="text-xs text-orange-600 mt-1">
                    Pick a cause — only client-caused failures count against the client.
                  </p>
                )}
              </div>
            )}

            {/* Optional photos on a failed attempt — closed shop, refusal,
                damage. Never required: a written reason is the only gate. */}
            {stopUpdateStatus === 'failed'
              && stopUpdateIndex !== null
              && !selectedRoute?.stops?.[stopUpdateIndex]?.is_custom
              && selectedRoute?.stops?.[stopUpdateIndex]?.order_id && (
              <div>
                <OrderPhotoUpload
                  orderId={selectedRoute.stops[stopUpdateIndex].order_id}
                  kind="delivery"
                  testId={`failed-photo-${selectedRoute.stops[stopUpdateIndex].order_id}`}
                  photos={stopUpdatePhotos}
                  onPhotosChange={setStopUpdatePhotos}
                  canRemove
                  label="Photos of the failed attempt (optional)"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Optional, but a photo of a closed shop or damaged goods settles most questions later.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setStopUpdateOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateStop}
              disabled={updating || (stopUpdateStatus === 'failed' && (!stopUpdateNotes.trim() || !stopUpdateCause))}
              className={stopUpdateStatus === 'delivered' ? 'bg-green-500 hover:bg-green-600' : 'bg-orange-500 hover:bg-orange-600'}
            >
              {(() => {
                if (updating) return 'Updating...';
                if (stopUpdateStatus === 'delivered') return 'Confirm Delivered';
                return 'Mark as Failed';
              })()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pickup reconciliation (driver count) */}
      {reconcileStop && (
        <PickupReconciliationDialog
          open={!!reconcileStop}
          onOpenChange={(open) => !open && setReconcileStop(null)}
          stop={reconcileStop.stop}
          routeId={selectedRoute?.id}
          onSubmitted={() => {
            setReconcileStop(null);
            refreshSelectedRoute();
          }}
        />
      )}

      {/* Complete delivery (photo gate) */}
      {completeStop && (
        <CompleteDeliveryDialog
          open={!!completeStop}
          onOpenChange={(open) => !open && setCompleteStop(null)}
          stop={completeStop.stop}
          routeId={selectedRoute?.id}
          stopIndex={completeStop.index}
          onCompleted={() => {
            setCompleteStop(null);
            refreshSelectedRoute();
          }}
        />
      )}
    </div>
  );
};

export default StaffDelivery;
